import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import {
  Check,
  ChevronsUpDown,
  ExternalLink,
  Lock,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sparkles,
  Store,
  UserCog,
} from 'lucide-react';
import {
  Avatar,
  Badge,
  Kbd,
  List,
  ListItem,
  Menu,
  Sheet,
  ThemeToggle,
  Tooltip,
  cn,
  springs,
  type MenuItem,
} from '@dezza/ui';
import { useUpgrade } from '../lib/access';
import { useAuth, useMe } from '../lib/auth';
import { matchNavItem, useNav, useOrdersBadge, type VisibleNavGroup, type VisibleNavItem } from './nav';
import { useShell } from './shell-store';

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** Texto curto do plano para o selo ("Pro", "Pro · teste"). */
function usePlanLabel(): string {
  const me = useMe();
  const ent = me.entitlements;
  return ent.status === 'trialing' ? `${ent.planName} · teste` : ent.planName;
}

/** Clique num item com cadeado: abre o upgrade em vez de navegar. */
function useItemClick(onNavigate?: () => void) {
  const upgrade = useUpgrade();
  return (item: VisibleNavItem) => (e: MouseEvent) => {
    if (item.locked && item.feature) {
      e.preventDefault();
      onNavigate?.();
      upgrade({ feature: item.feature });
      return;
    }
    onNavigate?.();
  };
}

// ---------------------------------------------------------------- Troca de loja
/** Itens do menu da loja: lojas (se houver mais de uma), plano, dados da loja e catálogo. */
function useStoreMenuItems(): (MenuItem | 'separator')[] {
  const { me, switchCompany } = useAuth();
  const navigate = useNavigate();
  if (!me?.company) return [];
  const perms = me.membership?.permissions ?? [];
  const items: (MenuItem | 'separator')[] = [];
  if (me.companies.length > 1) {
    for (const c of me.companies) {
      const current = c.id === me.company.id;
      items.push({
        label: (
          <span className="flex min-w-0 items-center justify-between gap-3">
            <span className="truncate">{c.name}</span>
            {current ? (
              <Check className="size-4 shrink-0 text-primary-strong" aria-label="Loja atual" />
            ) : null}
          </span>
        ),
        icon: <Avatar name={c.name} src={c.logoUrl} size={22} />,
        onSelect: () => {
          if (current) return;
          void switchCompany(c.id).then(() => navigate('/inicio'));
        },
      });
    }
    items.push('separator');
  }
  if (perms.includes('settings.manage'))
    items.push({ label: 'Dados da loja', icon: <Store />, onSelect: () => navigate('/configuracoes/loja') });
  if (perms.includes('billing.manage'))
    items.push({ label: 'Meu plano', icon: <Sparkles />, onSelect: () => navigate('/configuracoes/plano') });
  if (me.catalog?.isPublished && me.catalog.url) {
    const url = me.catalog.url;
    items.push({
      label: 'Ver catálogo online',
      icon: <ExternalLink />,
      onSelect: () => window.open(url, '_blank', 'noopener'),
    });
  }
  return items;
}

function StoreSwitcher({ collapsed }: { collapsed: boolean }) {
  const me = useMe();
  const plan = usePlanLabel();
  const items = useStoreMenuItems();
  const interactive = items.length > 0;
  const content = (
    <>
      <Avatar
        name={me.company.name}
        src={me.company.logoUrl}
        size={collapsed ? 36 : 38}
        className="rounded-[11px] ring-1 ring-line"
      />
      {collapsed ? (
        <span className="sr-only">{me.company.name}</span>
      ) : (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-semibold leading-tight tracking-[-0.01em] text-ink">
              {me.company.name}
            </span>
            <span className="mt-1 flex min-w-0 items-center gap-1.5">
              <Badge tone="primary" size="sm" className="shrink-0">
                {plan}
              </Badge>
              <span className="truncate text-xs text-ink-3">{firstName(me.user.name)}</span>
            </span>
          </span>
          {interactive ? (
            <ChevronsUpDown
              className="size-4 shrink-0 text-ink-3 transition-colors group-hover:text-ink-2"
              aria-hidden="true"
            />
          ) : null}
        </>
      )}
    </>
  );
  const boxClass = cn(
    'group flex w-full items-center gap-2.5 rounded-xl text-left',
    collapsed ? 'justify-center p-1.5' : 'p-2',
  );
  if (!interactive) return <div className={boxClass}>{content}</div>;
  const trigger = (
    <button
      type="button"
      className={cn(
        boxClass,
        'outline-none transition-colors duration-150 hover:bg-ink/[0.05] focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-ink/[0.05]',
      )}
    >
      {content}
    </button>
  );
  const menu = (
    <Menu
      trigger={trigger}
      items={items}
      align="start"
      label={`Loja: ${me.company.name}. Abrir opções da loja`}
    />
  );
  return collapsed ? (
    <Tooltip content={me.company.name} side="right">
      <span className="block">{menu}</span>
    </Tooltip>
  ) : (
    menu
  );
}

// ---------------------------------------------------------------- Item do menu lateral
function SidebarItem({
  item,
  active,
  collapsed,
  badge,
  onClick,
}: {
  item: VisibleNavItem;
  active: boolean;
  collapsed: boolean;
  badge: number;
  onClick: (e: MouseEvent) => void;
}) {
  const reduce = useReducedMotion();
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={
        collapsed
          ? `${item.label}${badge ? `, ${badge} em aberto` : ''}${item.locked ? ', disponível em outro plano' : ''}`
          : undefined
      }
      className={cn(
        'group relative flex h-[34px] items-center gap-2.5 rounded-[10px] text-[14px] font-medium outline-none transition-colors duration-150',
        'focus-visible:ring-2 focus-visible:ring-ring',
        collapsed ? 'mx-auto w-11 justify-center' : 'px-2',
        active ? 'bg-ink/[0.06] text-ink' : 'text-ink-2 hover:bg-ink/[0.04] hover:text-ink',
      )}
    >
      {active ? (
        <motion.span
          layoutId="sidebar-active"
          transition={reduce ? { duration: 0 } : springs.snappy}
          aria-hidden="true"
          className={cn(
            'absolute w-[3px] rounded-full bg-primary',
            collapsed ? '-left-3 inset-y-2.5' : '-left-3 inset-y-2',
          )}
        />
      ) : null}
      <span
        className={cn(
          'relative flex size-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
          item.emphasis
            ? 'bg-primary text-on-primary shadow-btn'
            : active
              ? 'text-primary-strong'
              : 'text-ink-3 group-hover:text-ink-2',
        )}
      >
        <Icon className="size-[18px]" strokeWidth={active || item.emphasis ? 2.25 : 2} aria-hidden="true" />
        {collapsed && badge > 0 ? (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-on-primary ring-2 ring-surface">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
        {collapsed && item.isNew && !item.locked ? (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-accent ring-2 ring-surface"
          />
        ) : null}
        {collapsed && item.locked ? (
          <span
            aria-hidden="true"
            className="absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full bg-surface text-ink-3 ring-1 ring-line"
          >
            <Lock className="size-2" strokeWidth={3} />
          </span>
        ) : null}
      </span>
      {collapsed ? null : (
        <>
          <span className={cn('min-w-0 flex-1 truncate', (active || item.emphasis) && 'font-semibold')}>
            {item.label}
          </span>
          {item.isNew && !item.locked ? (
            <Badge tone="new" size="sm" className="shrink-0">
              Novo
            </Badge>
          ) : null}
          {item.locked ? (
            <Lock className="size-3.5 shrink-0 text-ink-3" aria-label="Disponível em outro plano" />
          ) : null}
          {badge > 0 ? (
            <span className="tabular flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-on-primary">
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null}
        </>
      )}
    </Link>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip content={item.label} side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

/**
 * Menu lateral do desktop: troca de loja no topo, navegação agrupada e botão para recolher em
 * trilho de ícones (72 px, com dicas). Usa as superfícies do tema (claro e escuro), sem bloco preto.
 */
export function Sidebar() {
  const location = useLocation();
  const groups = useNav();
  const collapsed = useShell((s) => s.collapsed);
  const toggle = useShell((s) => s.toggleCollapsed);
  const ordersBadge = useOrdersBadge();
  const onItemClick = useItemClick();
  const active = matchNavItem(
    location.pathname,
    groups.flatMap((g) => g.items),
  );

  return (
    <nav aria-label="Menu principal" className="flex h-full flex-col border-r border-line bg-surface">
      <div
        className={cn(
          'flex shrink-0 gap-1 pb-2 pt-3',
          collapsed ? 'flex-col items-center px-2' : 'items-center px-3',
        )}
      >
        <div className={collapsed ? undefined : 'min-w-0 flex-1'}>
          <StoreSwitcher collapsed={collapsed} />
        </div>
        <CollapseButton collapsed={collapsed} onClick={toggle} />
      </div>
      <div
        className={cn(
          'scrollbar-thin min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4',
          collapsed ? 'px-2' : 'px-3',
        )}
      >
        {groups.map((g, gi) => (
          <div key={g.key} className={gi ? 'mt-3.5' : 'mt-1'} role="group" aria-label={g.label}>
            {collapsed ? (
              gi ? (
                <div aria-hidden="true" className="mx-3 mb-3 h-px bg-line" />
              ) : null
            ) : (
              <div aria-hidden="true" className="eyebrow mb-1 px-2 text-ink-3">
                {g.label}
              </div>
            )}
            <ul className="space-y-px">
              {g.items.map((item) => (
                <SidebarItem
                  key={item.key}
                  item={item}
                  active={active?.key === item.key}
                  collapsed={collapsed}
                  badge={item.badge === 'orders' ? ordersBadge : 0}
                  onClick={onItemClick(item)}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

function CollapseButton({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const label = collapsed ? 'Expandir menu' : 'Recolher menu';
  return (
    <Tooltip
      side={collapsed ? 'right' : 'bottom'}
      content={
        <span className="flex items-center gap-2">
          {label}
          <Kbd className="border-white/20 bg-white/10 text-white/80 shadow-none">[</Kbd>
        </span>
      }
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-keyshortcuts="["
        className={cn(
          'flex shrink-0 items-center justify-center rounded-[10px] text-ink-3 outline-none transition-colors duration-150 hover:bg-ink/[0.05] hover:text-ink focus-visible:ring-2 focus-visible:ring-ring',
          collapsed ? 'h-8 w-11' : 'size-8',
        )}
      >
        <Icon className="size-[18px]" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------- Celular: folha "Mais"
function SheetTile({
  item,
  active,
  badge,
  onClick,
}: {
  item: VisibleNavItem;
  active: boolean;
  badge: number;
  onClick: (e: MouseEvent) => void;
}) {
  const Icon = item.icon;
  return (
    <li>
      <Link
        to={item.to}
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        aria-label={`${item.label}${badge ? `, ${badge} em aberto` : ''}${item.locked ? ', disponível em outro plano' : ''}${item.isNew && !item.locked ? ', novo' : ''}`}
        className="group flex min-h-[84px] flex-col items-center gap-1.5 rounded-2xl px-1 pb-2 pt-2.5 text-center outline-none transition-colors duration-150 active:bg-ink/[0.06] focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          className={cn(
            'relative flex size-12 items-center justify-center rounded-[14px] transition-colors duration-150',
            item.emphasis
              ? 'bg-primary text-on-primary shadow-btn'
              : active
                ? 'bg-primary-soft text-primary-strong ring-1 ring-inset ring-primary/25'
                : 'bg-surface-2 text-ink-2 ring-1 ring-inset ring-line',
          )}
        >
          <Icon className="size-[22px]" strokeWidth={active ? 2.25 : 2} aria-hidden="true" />
          {badge > 0 ? (
            <span className="tabular absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-on-primary ring-2 ring-surface">
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null}
          {item.locked ? (
            <span className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full bg-surface text-ink-3 shadow-control ring-1 ring-line">
              <Lock className="size-3" strokeWidth={2.5} aria-hidden="true" />
            </span>
          ) : null}
          {item.isNew && !item.locked ? (
            <span
              aria-hidden="true"
              className="absolute -right-2 -top-2 rounded-full bg-accent px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-on-accent ring-2 ring-surface"
            >
              Novo
            </span>
          ) : null}
        </span>
        <span
          className={cn(
            'line-clamp-2 text-[12px] leading-tight',
            active ? 'font-semibold text-ink' : 'font-medium text-ink-2',
          )}
          aria-hidden="true"
        >
          {item.label}
        </span>
      </Link>
    </li>
  );
}

function SheetGroups({ groups, onNavigate }: { groups: VisibleNavGroup[]; onNavigate: () => void }) {
  const location = useLocation();
  const ordersBadge = useOrdersBadge();
  const onItemClick = useItemClick(onNavigate);
  const active = matchNavItem(
    location.pathname,
    groups.flatMap((g) => g.items),
  );
  return (
    <>
      {groups.map((g) => (
        <section key={g.key} aria-label={g.label} className="mt-4">
          <h3 className="eyebrow mb-1 px-1 text-ink-3">{g.label}</h3>
          <ul className="grid grid-cols-4 gap-x-1">
            {g.items.map((item) => (
              <SheetTile
                key={item.key}
                item={item}
                active={active?.key === item.key}
                badge={item.badge === 'orders' ? ordersBadge : 0}
                onClick={onItemClick(item)}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

/**
 * Celular: folha com a navegação completa (aba "Mais" e botão "Abrir menu" do topo).
 * Loja e plano no topo, busca, grupos em grade de toque (alvos grandes), tema e conta no fim.
 */
export function NavSheet() {
  const { me, logout, switchCompany } = useAuth();
  const navigate = useNavigate();
  const open = useShell((s) => s.menuOpen);
  const setOpen = useShell((s) => s.setMenuOpen);
  const setPaletteOpen = useShell((s) => s.setPaletteOpen);
  const groups = useNav();
  const plan = usePlanLabel();
  const [switching, setSwitching] = useState(false);
  if (!me?.company) return null;
  const close = () => setOpen(false);
  const go = (to: string) => {
    close();
    navigate(to);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen} title="Menu" hideTitle className="bg-surface">
      <nav aria-label="Menu principal" className="pb-1">
        <div className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3 ring-1 ring-inset ring-line">
          <Avatar
            name={me.company.name}
            src={me.company.logoUrl}
            size={44}
            className="rounded-[13px] ring-1 ring-line"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[15px] font-semibold tracking-[-0.01em] text-ink">
              {me.company.name}
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <Badge tone="primary" size="sm" className="shrink-0">
                {plan}
              </Badge>
              <span className="truncate text-xs text-ink-3">{me.user.name}</span>
            </div>
          </div>
          {me.companies.length > 1 ? (
            <button
              type="button"
              onClick={() => setSwitching((v) => !v)}
              aria-expanded={switching}
              className="flex h-11 items-center gap-1 rounded-xl px-3 text-[13px] font-semibold text-primary-strong transition-colors hover:bg-ink/[0.05]"
            >
              Trocar
              <ChevronsUpDown className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {switching ? (
          <List inset className="mt-2">
            {me.companies.map((c) => (
              <ListItem
                key={c.id}
                leading={<Avatar name={c.name} src={c.logoUrl} size={32} />}
                title={c.name}
                chevron={false}
                trailing={
                  c.id === me.company?.id ? (
                    <Check className="size-5 text-primary-strong" aria-label="Loja atual" />
                  ) : null
                }
                onClick={async () => {
                  if (c.id === me.company?.id) return setSwitching(false);
                  await switchCompany(c.id);
                  setSwitching(false);
                  go('/inicio');
                }}
              />
            ))}
          </List>
        ) : null}

        <button
          type="button"
          onClick={() => {
            close();
            // Espera a folha fechar para a paleta receber o foco.
            window.setTimeout(() => setPaletteOpen(true), 180);
          }}
          className="mt-3 flex h-12 w-full items-center gap-3 rounded-control bg-surface px-3.5 text-left text-[15px] text-ink-3 shadow-control ring-1 ring-inset ring-line-strong transition-colors hover:text-ink-2"
        >
          <Search className="size-[18px] shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">Buscar telas, produtos, clientes</span>
        </button>

        <SheetGroups groups={groups} onNavigate={close} />

        <div className="mt-5 space-y-3 border-t border-line pt-4">
          <div className="flex items-center justify-between gap-3 px-1">
            <span className="text-[14px] font-medium text-ink-2">Aparência</span>
            <ThemeToggle size="sm" />
          </div>
          <List inset>
            <ListItem
              leading={<UserCog />}
              title="Minha conta e segurança"
              subtitle={me.user.email}
              onClick={() => go('/configuracoes/seguranca')}
            />
            <ListItem
              leading={<LogOut />}
              title="Sair"
              danger
              chevron={false}
              onClick={async () => {
                close();
                await logout();
                navigate('/entrar', { replace: true });
              }}
            />
          </List>
        </div>
      </nav>
    </Sheet>
  );
}

/** Botão do topo no celular: logo da loja que abre o menu completo. */
export function MenuAvatarButton({ className }: { className?: string }): ReactNode {
  const me = useMe();
  const setOpen = useShell((s) => s.setMenuOpen);
  const open = useShell((s) => s.menuOpen);
  return (
    <button
      type="button"
      aria-label="Abrir menu"
      aria-expanded={open}
      onClick={() => setOpen(true)}
      className={cn(
        'flex size-11 items-center justify-center rounded-full outline-none transition-[scale] duration-150 active:scale-[0.94] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:active:scale-100',
        className,
      )}
    >
      <Avatar
        name={me.company.name}
        src={me.company.logoUrl}
        size={32}
        className="rounded-[10px] ring-1 ring-white/15"
      />
    </button>
  );
}
